import React, { useState, useEffect } from "react";
import { Shield, ClipboardList } from "lucide-react";

const SecurityManagement = ({ activeSubTab }) => {
  const [securityData, setSecurityData] = useState([]);
  const [signInData, setSignInData] = useState([]);
  const [auditData, setAuditData] = useState([]);
  const [loading, setLoading] = useState({
    security: true,
    signin: false,
    audit: false
  });
  const [error, setError] = useState(null);

  // Fetch security data
  useEffect(() => {
    const fetchSecurityData = async () => {
      try {
        setLoading(prev => ({...prev, security: true}));
        const response = await fetch("/api/security/get_security");
        if (!response.ok) throw new Error('Failed to fetch security data');
        
        const data = await response.json();
        const dataArray = data && typeof data === 'object' && !Array.isArray(data) 
          ? Object.keys(data).map(key => ({
              securityType: key,
              description: data[key],
              appliesTo: getAppliesToField(key)
            }))
          : Array.isArray(data) ? data : [];
          
        setSecurityData(dataArray);
      } catch (err) {
        console.error('Error fetching security data:', err);
        setError("Failed to fetch security data");
      } finally {
        setLoading(prev => ({...prev, security: false}));
      }
    };

    if (!activeSubTab || activeSubTab === 'security') {
      fetchSecurityData();
    }
  }, [activeSubTab]);

  // Fetch sign-in logs when needed
  useEffect(() => {
    if (activeSubTab === 'signin') {
      const fetchSignInData = async () => {
        try {
          setLoading(prev => ({...prev, signin: true}));
          const response = await fetch("/api/security/get_signin");
          if (!response.ok) throw new Error('Failed to fetch sign-in logs');
          
          const data = await response.json();
          const parsedData = typeof data.signin === 'string' 
            ? JSON.parse(data.signin) 
            : data.signin || [];
            
          setSignInData(parsedData.sort((a, b) => new Date(b.Date) - new Date(a.Date)));
        } catch (err) {
          console.error('Error fetching sign-in logs:', err);
          setError("Failed to fetch sign-in logs");
        } finally {
          setLoading(prev => ({...prev, signin: false}));
        }
      };

      fetchSignInData();
    }
  }, [activeSubTab]);

  // Fetch audit logs when needed
  useEffect(() => {
    if (activeSubTab === 'audit') {
      const fetchAuditData = async () => {
        try {
          setLoading(prev => ({...prev, audit: true}));
          const response = await fetch("/api/security/get_audit");
          if (!response.ok) throw new Error('Failed to fetch audit logs');
          
          const data = await response.json();
          let parsedData = [];
          if (data.signin) {
            parsedData = typeof data.signin === 'string' 
              ? JSON.parse(data.signin) 
              : data.signin;
          }
          setAuditData(parsedData.sort((a, b) => new Date(b.Date) - new Date(a.Date)));
        } catch (err) {
          console.error('Error fetching audit logs:', err);
          setError("Failed to fetch audit logs");
        } finally {
          setLoading(prev => ({...prev, audit: false}));
        }
      };

      fetchAuditData();
    }
  }, [activeSubTab]);

  const getAppliesToField = (securityType) => {
    const mapping = {
      'Authentication': 'Login, Access Control',
      'Authorization': 'Applications, APIs, Data Layers',
      'Encryption (At Rest)': 'Storage, Databases',
      'Encryption (In Transit)': 'APIs, Web Traffic, File Transfers',
      'Role-Based Access Control (RBAC)': 'Admin Consoles, Applications',
      'Least Privilege': 'All User Roles',
      'Audit Logging': 'Admin Panel, API Gateways',
      'Anomaly Detection': 'Security Operations, Monitoring',
      'Single Sign-On (SSO)': 'Identity Management, Federated Access',
      'Zero Trust': 'Modern Enterprise Architectures',
      'Data Loss Prevention (DLP)': 'File Sharing, Communication Tools'
    };
    return mapping[securityType] || 'System-wide';
  };

  const getStatusColor = (status) => {
    return status === 'Success' ? 'text-green-600' : 'text-red-600';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const renderSecurityOverview = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-200 bg-gray-50">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <Shield className="w-5 h-5 mr-2 text-blue-600" />
          Security Overview
        </h2>
        <p className="text-sm text-gray-500 mt-1">Current security measures implemented in the system</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xss font-bold font-medium text-black uppercase tracking-wider">
                Security Type
              </th>
              <th className="px-6 py-3 text-left text-xss font-bold font-medium text-black uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xss font-bold font-medium text-black uppercase tracking-wider">
                Applies To
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {securityData.length > 0 ? (
              securityData.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.securityType}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {item.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.appliesTo}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">
                  {loading.security ? 'Loading security data...' : 'No security data available'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderSignInLogs = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-200 bg-gray-50">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <ClipboardList className="w-5 h-5 mr-2 text-blue-600" />
          Sign-in Logs
        </h2>
        <p className="text-sm text-gray-500 mt-1">Recent user authentication attempts</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Request ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Application
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Error Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                IP Address
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {signInData.length > 0 ? (
              signInData.map((log, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ width: '15%' }}>
                    {formatDate(log.Date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ width: '24%' }}>
                    {log['Request ID']}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ width: '15%' }}>
                    {log.User}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ width: '15%' }}>
                    {log.Application}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${getStatusColor(log.Status)}`}>
                    {log.Status}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ width: '15%' }}>
                    {log['Sign-in error code'] || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ width: '15%' }}>
                    {log['IP address']}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {log.Location}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="px-6 py-4 text-center text-sm text-gray-500">
                  {loading.signin ? 'Loading sign-in logs...' : 'No sign-in logs available'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderAuditLogs = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-200 bg-gray-50">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <ClipboardList className="w-5 h-5 mr-2 text-blue-600" />
          Audit Logs
        </h2>
        <p className="text-sm text-gray-500 mt-1">Recent system activities and changes</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Service
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Activity
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status Reason
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {auditData.length > 0 ? (
              auditData.map((log, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ width: '15%' }}>
                    {formatDate(log.Date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ width: '15%' }}>
                    {log.Service}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ width: '15%' }}>
                    {log.Category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ width: '15%' }}>
                    {log.Activity}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${getStatusColor(log.Status)}`}>
                    {log.Status}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {log['Status reason']}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                  {loading.audit ? 'Loading audit logs...' : 'No audit logs available'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderContent = () => {
    if (error) {
      return (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      );
    }

    switch (activeSubTab) {
      case 'signin':
        return renderSignInLogs();
      case 'audit':
        return renderAuditLogs();
      case 'security':
      default:
        return renderSecurityOverview();
    }
  };

  return (
    <div>
      {loading.security && !activeSubTab && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}
      {renderContent()}
    </div>
  );
};

export default SecurityManagement;